import { useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import {
  AlertTriangle, CalendarClock, CheckCircle2, Clock, Calendar as CalendarIcon,
  CornerDownRight, Info, ListChecks, Phone, Wallet, ExternalLink,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { useClientTab } from "@/hooks/useClients";
import { useClient360Timeline } from "@/hooks/useClient360Tasks";
import { TaskActionBar } from "@/components/client360/TaskActionBar";
import { MeetingActionBar } from "@/components/client360/MeetingActionBar";
import { TaskCompleteModal } from "@/components/task/TaskCompleteModal";
import { CreateInternalRequestDrawer } from "@/components/requests/CreateInternalRequestDrawer";
import { OUTCOME_LABEL, type TaskOutcome, type TaskSubtype, type TimelineEvent } from "@/api/client360Tasks";
import type { Client360Envelope } from "@/api/clients";

function useHoldingId(): string {
  const { holdingId } = useParams<{ holdingId: string }>();
  return holdingId ?? "";
}
function useEnv() { return useOutletContext<Client360Envelope>(); }

// ── Активності (Timeline read-only) ─────────────────────────────────────────

const DOT_TONE: Record<string, string> = {
  green: "bg-success-foreground", amber: "bg-warning-foreground", blue: "bg-info-foreground", gray: "bg-muted-foreground",
};

export function ClientTimelineTab() {
  const id = useHoldingId();
  const env = useEnv();
  const navigate = useNavigate();
  const aa = env?.allowedActions ?? {};
  const q = useClient360Timeline(id, true);
  const [searchParams] = useSearchParams();
  const deepTarget = searchParams.get("action");
  const [reportSignal, setReportSignal] = useState<{ interactionId: string; summary: string; nonce: number } | null>(null);
  // #9 S5 — TZ §13: «створити запит» з Client360 (canonical drawer, prefill holding).
  const [requestOpen, setRequestOpen] = useState(false);

  const overdueAmount = env?.biStrip?.holdingOverdueTotal ?? null;
  const openSignal = useMemo(
    () => (deepTarget === "call_log" || deepTarget === "task_create" || deepTarget === "follow_up"
      ? { target: deepTarget as "call_log" | "task_create" | "follow_up", nonce: 1 }
      : null),
    [deepTarget],
  );

  const onEvent = (ev: TimelineEvent) => {
    if (ev.reportRequired && ev.source.entityType === "interaction") {
      setReportSignal({ interactionId: ev.source.entityId, summary: ev.detail || ev.title, nonce: Date.now() });
    } else if (ev.source.entityType === "task") {
      // D-150 Q1: status-agnostic canonical drawer (F3 deep-link), NOT the
      // open-tasks tab — a Completed task exists only on this route.
      navigate(`/tasks?open=${ev.source.entityId}`);
    } else if (ev.source.entityType === "meeting" || ev.source.entityType === "interaction") {
      // D-150 Q1: canonical meeting drawer (D-137b). Reachable only for
      // interaction-grain events — native "meeting" rows get canOpen=false.
      navigate(`/meetings?open=${ev.source.entityId}`);
    } else if (ev.source.entityType === "internal_request") {
      navigate("/requests");
    }
  };

  return (
    <div className="p-6 md:p-8">
      <div className="mb-1 flex flex-wrap items-center gap-3">
        <h2 className="mr-auto text-base font-semibold">
          Історія <span className="text-sm font-normal text-muted-foreground">/ Timeline (read-only · журнал подій клієнта)</span>
        </h2>
        <button type="button" onClick={() => setRequestOpen(true)}
          className="rounded-md border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
          Запит у відділ
        </button>
        <MeetingActionBar holdingId={id} aa={aa} openReportSignal={reportSignal} />
      </div>
      <CreateInternalRequestDrawer
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        holdingId={id}
        holdingName={env?.header?.displayName ?? env?.header?.name}
        sourceModule="client360"
        sourceEntityId={id}
      />
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <p className="mr-auto flex items-center gap-1.5 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5" /> Read-only журнал подій клієнта — задачі, дзвінки, follow-up, зустрічі, звіти. «Активність» вручну не створюється.
        </p>
        <TaskActionBar
          holdingId={id}
          canCreateTask={!!aa.canCreateTask}
          canLogCall={!!aa.canLogCall}
          overdueAmount={overdueAmount}
          openSignal={openSignal}
        />
      </div>

      {q.isPending ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />)}</div>
      ) : (q.data?.items.length ?? 0) === 0 ? (
        <div className="rounded-lg border border-dashed px-4 py-10 text-center text-sm text-muted-foreground">
          Подій ще немає. Почніть з «Зафіксувати дзвінок» або «+ Задача».
        </div>
      ) : (
        <ol className="relative ml-1 border-l pl-6">
          {q.data!.items.map((ev) => (
            <li key={ev.id} className="relative mb-5 last:mb-0">
              <span className={cn("absolute -left-[1.6rem] top-1.5 h-3 w-3 rounded-full border-2 border-background", DOT_TONE[ev.tone] ?? "bg-muted-foreground")} />
              <div className="flex items-start gap-2">
                <EventIcon kind={ev.kind} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold">{ev.title}</span>
                    {ev.result && (
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                        результат: {OUTCOME_LABEL[ev.result as TaskOutcome] ?? ev.result}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[12.5px] text-muted-foreground">
                    <span className="truncate">{ev.detail}</span>
                    {ev.at && <span>· {fmtAt(ev.at)}</span>}
                    {ev.reportRequired && ev.source.entityType === "interaction" ? (
                      <button onClick={() => onEvent(ev)} className="inline-flex items-center gap-0.5 font-semibold text-warning-foreground hover:underline">
                        Заповнити звіт <ExternalLink className="h-3 w-3" />
                      </button>
                    ) : (ev.source.entityType === "task" || ev.source.entityType === "meeting" || ev.source.entityType === "interaction") && ev.canOpen === true ? (
                      <button onClick={() => onEvent(ev)} className="inline-flex items-center gap-0.5 font-semibold text-primary hover:underline">
                        Відкрити <ExternalLink className="h-3 w-3" />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function EventIcon({ kind }: { kind: string }) {
  const Icon =
    kind === "call_logged" ? Phone
    : kind === "payment_promise_created" ? Wallet
    : kind === "follow_up_created" ? CornerDownRight
    : kind === "meeting" || kind === "meeting_report" ? CalendarClock
    : ListChecks;
  return <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />;
}

function fmtAt(iso: string): string {
  const d = new Date(iso.replace(" ", "T") + (iso.includes("Z") || iso.includes("+") ? "" : "Z"));
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

// ── Задачі (open tasks, holding grain) ──────────────────────────────────────

interface TaskItem {
  id: string; title: string; dueDate: string | null; deadlineLabel: string;
  bucket: "overdue" | "today" | "week" | "later"; overdue: boolean;
  priority: "high" | "medium" | "low"; subtype: TaskSubtype; status: string;
  assigneeName: string | null;
}

const BUCKETS = [
  { k: "overdue", label: "Прострочені", icon: AlertTriangle, tone: "text-destructive" },
  { k: "today", label: "Сьогодні", icon: CalendarClock, tone: "text-warning-foreground" },
  { k: "week", label: "Цей тиждень", icon: CalendarIcon, tone: "text-primary" },
  { k: "later", label: "Пізніше", icon: Clock, tone: "text-muted-foreground" },
] as const;

export function ClientTasksTab() {
  const id = useHoldingId();
  const env = useEnv();
  const aa = env?.allowedActions ?? {};
  const q = useClientTab<TaskItem>(id, "tasks", true);
  const [completing, setCompleting] = useState<{ id: string; title: string; subtype: TaskSubtype } | null>(null);

  const items = q.data?.items ?? [];
  const grouped = useMemo(() => {
    const g: Record<string, TaskItem[]> = { overdue: [], today: [], week: [], later: [] };
    for (const t of items) (g[t.bucket] ?? g.later).push(t);
    return g;
  }, [items]);

  return (
    <div className="p-6 md:p-8">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h2 className="mr-auto text-base font-semibold">Задачі <span className="text-sm font-normal text-muted-foreground">· відкриті</span></h2>
        <TaskActionBar holdingId={id} canCreateTask={!!aa.canCreateTask} canLogCall={!!aa.canLogCall} />
      </div>

      {q.isPending ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />)}</div>
      ) : q.data?.restricted ? (
        <div className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">Задачі недоступні (немає доступу).</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed px-4 py-10 text-center text-sm text-muted-foreground">Відкритих задач немає.</div>
      ) : (
        <div className="space-y-5">
          {BUCKETS.map((b) => {
            const list = grouped[b.k];
            if (!list || list.length === 0) return null;
            const Icon = b.icon;
            return (
              <div key={b.k}>
                <div className={cn("mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide", b.tone)}>
                  <Icon className="h-3.5 w-3.5" /> {b.label} <span className="text-muted-foreground">({list.length})</span>
                </div>
                <div className="space-y-1.5">
                  {list.map((t) => (
                    <div key={t.id} className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5">
                      <SubtypeIcon subtype={t.subtype} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{t.title}</div>
                        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                          <span className={cn(t.overdue && "font-semibold text-destructive")}>{t.deadlineLabel}</span>
                          {t.assigneeName && <span>· {t.assigneeName}</span>}
                          {t.priority === "high" && <span className="rounded bg-destructive/10 px-1.5 text-[11px] font-medium text-destructive">Високий</span>}
                        </div>
                      </div>
                      {aa.canCompleteTask && (
                        <button
                          type="button"
                          onClick={() => setCompleting({ id: t.id, title: t.title, subtype: t.subtype })}
                          className="inline-flex shrink-0 items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/10"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" /> Виконано
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* canonical outcome-required completion (D-031) */}
      <TaskCompleteModal
        open={completing !== null}
        onClose={() => setCompleting(null)}
        task={completing ? { ...completing, holdingId: id } : null}
      />
    </div>
  );
}

function SubtypeIcon({ subtype }: { subtype: TaskSubtype }) {
  const Icon = subtype === "call" ? Phone : subtype === "follow_up" ? CornerDownRight : ListChecks;
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
      <Icon className="h-4 w-4" aria-hidden />
    </div>
  );
}
